"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: 1,
  title: "First-Time Buyer Mortgages",
  description: "Navigating your first home purchase requires expert guidance. We specialize in low-deposit options and government schemes designed to get you through your first front door with confidence.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/first_time_home_buyer.png"
}, {
  id: 2,
  title: "Refinancing & Equity Release",
  description: "Unlock the potential within your current property. Whether you're looking to lower your monthly payments or access capital for renovations, our refinancing strategies are tailored to your goals.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/home_refinancing_consultation.png"
}, {
  id: 3,
  title: "Investment Property Loans",
  description: "Build your portfolio with sophisticated financing. We provide access to competitive buy-to-let rates and interest-only structures designed to maximize your rental yield and long-term growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/investment_property_portfolio.png"
}, {
  id: 4,
  title: "Fixed-Rate Security Plans",
  description: "Protect yourself against market volatility with our premium fixed-rate solutions. Lock in your interest rate for 2, 5, or 10 years to ensure absolute budget certainty and peace of mind.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/fixed_rate_mortgage_contract.png"
}, {
  id: 5,
  title: "Bridging & Short-Term Finance",
  description: "Fast-acting capital for time-sensitive opportunities. Our bridging loans provide the liquidity you need to secure a property before your current one sells, with flexible exit strategies.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/commercial_bridging_finance.png"
}];
const serviceSpecs = [{
  serviceId: 1,
  spec: "Low Deposit Options"
}, {
  serviceId: 1,
  spec: "Step-by-Step Guidance"
}, {
  serviceId: 1,
  spec: "Stamp Duty Advice"
}, {
  serviceId: 2,
  spec: "Rate Comparison"
}, {
  serviceId: 2,
  spec: "Debt Consolidation"
}, {
  serviceId: 2,
  spec: "Equity Assessment"
}, {
  serviceId: 3,
  spec: "Portfolio Lending"
}, {
  serviceId: 3,
  spec: "Tax-Efficient Structures"
}, {
  serviceId: 3,
  spec: "High LTV Options"
}, {
  serviceId: 4,
  spec: "Guaranteed Payments"
}, {
  serviceId: 4,
  spec: "Inflation Protection"
}, {
  serviceId: 4,
  spec: "Long-Term Stability"
}, {
  serviceId: 5,
  spec: "Rapid Approval"
}, {
  serviceId: 5,
  spec: "Flexible Repayment"
}, {
  serviceId: 5,
  spec: "Auction Finance"
}];
function ServiceBlock({
  service,
  index,
  specs
}: {
  service: any;
  index: number;
  specs: any[];
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} className={cn("grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      <div className={cn("relative w-full aspect-[4/3] rounded-md overflow-hidden shadow-xl border-t-2 border-secondary", isEven ? "lg:order-1" : "lg:order-2")}>
        <img src={service.image} alt={service.title} className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" />
        <div className="absolute inset-0 ring-1 ring-inset ring-primary/10 rounded-md pointer-events-none"></div>
      </div>

      <div className={cn("flex flex-col gap-8", isEven ? "lg:order-2" : "lg:order-1")}>
        <div>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            {service.title}
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            {service.description}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {specs.map((spec, i) => <Badge key={i} data-index={i} variant="secondary" className="text-sm py-1.5 px-4 rounded-md bg-secondary/10 backdrop-blur-sm border-secondary/20 text-secondary-foreground font-medium">
              {spec.spec}
            </Badge>)}
        </div>

        <div className="pt-4">
          <Button asChild size="lg" className="rounded-md px-8 h-12 text-base shadow-lg shadow-primary/20 hover:shadow-secondary/40 transition-all w-fit bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/ContactPage">
              <MessageCircle className="mr-2 size-5" />
              Consult an Expert
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function MortgageSolutionsSuite() {
  return <section data-section-id="233595" id="mortgage-solutions-suite" className="bg-background text-foreground py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-24 md:mb-32">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground tracking-tight mb-6">
            Premier <span className="text-primary">Mortgage Solutions</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            Expertly crafted financial pathways designed for your unique property journey. From first steps to expansive portfolios, we provide the precision and trust you deserve.
          </p>
        </div>

        <div className="flex flex-col gap-32 md:gap-40">
          {services.map((service, index) => <div key={service.id} data-index={index}>
              <ServiceBlock service={service} index={index} specs={serviceSpecs.filter(s => s.serviceId === service.id)} />
            </div>)}
        </div>
      </div>
    </section>;
}